import { FinancialRecordInput, FinancialRecord } from '../types/data/financialRecord';
import { 
  validateFinancialInputs, 
  groupFinancialInputs, 
  convertInputsToRecords,
  convertInputsToRecordsWithDatabase,
  parseKPIValue 
} from './kpiUtils';
import { CSVProcessingResult } from '../types/data/csvProcessingResult';

export interface CSVColumnMapping {
  brand: number;
  dealer: number;
  year: number;
  month: number;
  kpiCode: number;
  value: number;
  // Optional metadata columns
  kpiName?: number;
  category?: number;
  subcategory?: number;
  units?: number;
}

/**
 * Process CSV data with the new KPI-based input format
 * Expected format: Brand, Dealer, Year, Month, KPI Code, Value, [KPI Name], [Category], [Subcategory], [Units]
 */
export const processKPIBasedCSV = (
  csvData: string[][],
  columnMapping: CSVColumnMapping
): CSVProcessingResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const inputs: FinancialRecordInput[] = [];
  
  // Store additional columns data
  const additionalColumnsMap = new Map<string, Map<string, string>>();
  
  // Identify which columns are not mapped to standard fields
  const mappedColumnIndices = new Set<number>();
  Object.values(columnMapping).forEach(idx => {
    if (idx !== undefined) {
      mappedColumnIndices.add(idx);
    }
  });
  
  // Get additional column headers
  const headers = csvData[0];
  const additionalColumnIndices: { index: number; name: string }[] = [];
  headers.forEach((header, index) => {
    if (!mappedColumnIndices.has(index) && header.trim() !== '') {
      additionalColumnIndices.push({ index, name: header.trim() });
    }
  });
  
  if (additionalColumnIndices.length > 0) {
    console.log(`Found ${additionalColumnIndices.length} additional columns:`, 
      additionalColumnIndices.map(col => col.name).join(', '));
  }

  // Process each row
  for (let i = 1; i < csvData.length; i++) {
    const row = csvData[i];
    
    // Skip empty rows
    if (row.every(cell => !cell || cell.trim() === '')) {
      continue;
    }
    
    try {
      // Extract required fields
      const brand = row[columnMapping.brand]?.trim();
      const dealer = row[columnMapping.dealer]?.trim();
      const yearStr = row[columnMapping.year]?.trim();
      const monthStr = row[columnMapping.month]?.trim();
      const kpiCode = row[columnMapping.kpiCode]?.trim();
      
      // Handle null values explicitly for KPI values
      let valueStr = row[columnMapping.value];
      if (valueStr === null || valueStr === undefined) {
                valueStr = "0";
      } else {
        valueStr = valueStr.trim();
      }
      
      // Validate required fields
      if (!brand || !dealer || !yearStr || !monthStr || !kpiCode) {
        errors.push(`Row ${i + 1}: Missing required fields (brand, dealer, year, month, kpiCode)`);
        continue;
      }
      
      // Parse year and month
      const year = parseInt(yearStr);
      let month = parseInt(monthStr);
      
      // Handle quarter format (Q1, Q2, etc.) by using the first month of the quarter
      if (isNaN(month) && monthStr.toUpperCase().startsWith('Q')) {
        const quarter = parseInt(monthStr.substring(1));
        if (!isNaN(quarter) && quarter >= 1 && quarter <= 4) {
          month = (quarter - 1) * 3 + 1; // Q1 = 1, Q2 = 4, Q3 = 7, Q4 = 10
        }
      }
      
      if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
        errors.push(`Row ${i + 1}: Invalid year or month format`);
        continue;
      }
      
      // Collect additional column data
      const recordKey = `${dealer}|${brand}|${year}|${month}`;
      if (!additionalColumnsMap.has(recordKey)) {
        additionalColumnsMap.set(recordKey, new Map<string, string>());
      }
      
      const recordAdditionalData = additionalColumnsMap.get(recordKey)!;
      additionalColumnIndices.forEach(col => {
        const value = row[col.index]?.trim();
        if (value) {
          recordAdditionalData.set(col.name, value);
        }
      });
      
      // Parse value
      const value = parseKPIValue(valueStr);
      
      // Get optional fields
      const kpiName = columnMapping.kpiName !== undefined && row[columnMapping.kpiName] 
        ? row[columnMapping.kpiName].trim() 
        : '';
        
      const category = columnMapping.category !== undefined && row[columnMapping.category] 
        ? row[columnMapping.category].trim() 
        : '';
        
      const subcategory = columnMapping.subcategory !== undefined && row[columnMapping.subcategory] 
        ? row[columnMapping.subcategory].trim() 
        : '';
        
      const unit = columnMapping.units !== undefined && row[columnMapping.units] 
        ? row[columnMapping.units].trim() 
        : '';
      
      // Find or create input for this dealer/brand/year/month combination
      let input = inputs.find(
        inp => inp.dealer === dealer && inp.brand === brand && inp.year === year && inp.month === month
      );
      
      if (!input) {
        input = {
          dealer,
          brand,
          year,
          month,
          kpis: []
        };
        inputs.push(input);
      }
      
      // Add KPI
      input.kpis.push({
        code: kpiCode,
        name: kpiName,
        value,
        category,
        subcategory,
        unit
      });
      
    } catch (error) {
      errors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  // Add additional data to each input
  inputs.forEach(input => {
    const recordKey = `${input.dealer}|${input.brand}|${input.year}|${input.month}`;
    const additionalData = additionalColumnsMap.get(recordKey);
    
    if (additionalData && additionalData.size > 0) {
      input.additionalData = Array.from(additionalData.entries()).map(([field, value]) => ({
        field,
        value
      }));
    }
  });

  return {
    inputs,
    errors,
    warnings
  };
};

/**
 * Auto-detect column mapping from CSV headers
 */
export const detectColumnMapping = (headers: string[]): Partial<CSVColumnMapping> => {
  const mapping: Partial<CSVColumnMapping> = {};
  
  headers.forEach((header, index) => {
    const normalizedHeader = header.toLowerCase().trim();
    
    // Required field mappings
    if (normalizedHeader.includes('brand')) {
      mapping.brand = index;
    } else if (normalizedHeader.includes('dealer')) {
      mapping.dealer = index;
    } else if (normalizedHeader.includes('year')) {
      mapping.year = index;
    } else if (normalizedHeader.includes('period') || normalizedHeader.includes('month')) {
      mapping.month = index;
    } else if ((normalizedHeader.includes('kpi') && normalizedHeader.includes('code')) || 
               normalizedHeader === 'kpi code') {
      mapping.kpiCode = index;
    } else if (normalizedHeader.includes('value') || normalizedHeader.includes('amount')) {
      mapping.value = index;
    }
    
    // Optional metadata field mappings - improved to match exact names that might be used
    else if ((normalizedHeader.includes('kpi') && normalizedHeader.includes('name')) || 
              normalizedHeader === 'kpi name' || normalizedHeader === 'kpiname') {
      mapping.kpiName = index;
    } else if ((normalizedHeader.includes('category') && !normalizedHeader.includes('sub')) || 
                normalizedHeader === 'kpi category' || normalizedHeader === 'kpicategory') {
      mapping.category = index;
    } else if (normalizedHeader.includes('subcategory') || 
                normalizedHeader.includes('sub-category') || 
                (normalizedHeader.includes('kpi') && normalizedHeader.includes('sub')) || 
                normalizedHeader === 'kpi subcategory' || 
                normalizedHeader === 'kpisubcategory') {
      mapping.subcategory = index;
    } else if (normalizedHeader.includes('unit') || 
                normalizedHeader.includes('measure') || 
                normalizedHeader === 'kpi units' || 
                normalizedHeader === 'kpiunits') {
      mapping.units = index;
    }
  });
  
  console.log('Auto-detected column mapping:', mapping);
  return mapping;
};

/**
 * Validate that all required columns are mapped
 */
export const validateColumnMapping = (mapping: Partial<CSVColumnMapping>): { valid: boolean; missingColumns: string[] } => {
  const requiredColumns: (keyof CSVColumnMapping)[] = ['brand', 'dealer', 'year', 'month', 'kpiCode', 'value'];
  const missingColumns: string[] = [];
  
  requiredColumns.forEach(col => {
    if (mapping[col] === undefined) {
      missingColumns.push(col);
    }
  });
  
  return {
    valid: missingColumns.length === 0,
    missingColumns
  };
};

/**
 * Process CSV data with the new KPI-based input format using database enrichment
 * Expected format: Brand, Dealer, Year, Month, KPI Code, Value, [KPI Name], [Category], [Subcategory], [Units]
 */
export const processKPIBasedCSVWithDatabase = async (
  csvData: string[][],
  columnMapping: CSVColumnMapping
): Promise<CSVProcessingResult> => {
  return processKPIBasedCSV(csvData, columnMapping);
}; 